import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useProductPassport } from '../hooks';
import Loader from 'src/components/Loader';
import NotFound from 'src/views/components/NotFound';
import { CheckCircleRounded } from '@mui/icons-material';
import { PanelBody, PanelHead, RowName } from './index.styles';
import ImpactGridTable from '../ImpactGrid/ImpactGridTable';
import { useTranslation } from 'react-i18next';
import colors from 'src/theme/colors';

export default function ProductPassport() {
  const { t } = useTranslation('lca', { keyPrefix: 'productPassport' });
  const { passportToken } = useParams();
  const { product, isLoading } = useProductPassport(passportToken as string);

  if (isLoading) {
    return <Loader />;
  }

  if (!product) {
    return <NotFound />;
  }
  return (
    <Box sx={{ ml: '48px', mr: '48px' }}>
      <Typography variant="h5" mt="16px" color={colors.darkGrey}>
        {t('title')}
      </Typography>
      <Typography variant="h1" mt="32px" mb="32px">
        {product.name}
      </Typography>

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box width="30%">
          <img
            width="100%"
            src={product.image_url}
            alt={product.name}
            style={{ borderRadius: '25px' }}
          />
        </Box>

        <Box width="65%">
          <PanelHead>
            <Typography variant="h4">{t('productOverview')}</Typography>
          </PanelHead>
          <PanelBody>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell width="30%">
                    <RowName>{t('gtin')}</RowName>
                  </TableCell>
                  <TableCell>{product.gtin_code}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <RowName>{t('brandName')}</RowName>
                  </TableCell>
                  <TableCell>{product.manufacturer_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <RowName>{t('weight')}</RowName>
                  </TableCell>
                  <TableCell>
                    {product.weight} {product.weight_unit}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <RowName>{t('basicMaterial')}</RowName>
                  </TableCell>
                  <TableCell>{product.basic_material}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <RowName>{t('dataCollectionPeriod')}</RowName>
                  </TableCell>
                  <TableCell>{product.data_collection_period}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </PanelBody>

          <PanelHead>
            <Typography variant="h4">{t('environmentalImpact')}</Typography>
          </PanelHead>
          <PanelBody overflow="auto">
            <ImpactGridTable
              diagramData={product.diagram}
              impactData={product.impacts}
            />
          </PanelBody>

          {product.materials.length > 0 && (
            <>
              <PanelHead>
                <Typography variant="h4">{t('materialComponents')}</Typography>
              </PanelHead>
              <PanelBody>
                <Table>
                  <TableBody>
                    {product.materials.map((component, index) => (
                      <TableRow key={index}>
                        <TableCell width="30%">
                          <RowName>{component.name}</RowName>
                        </TableCell>
                        <TableCell>
                          {component.percentage.toFixed(0)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </PanelBody>
            </>
          )}

          {product.certificates.length > 0 && (
            <>
              <PanelHead>
                <Typography variant="h4">{t('certifications')}</Typography>
              </PanelHead>
              <PanelBody>
                <Table>
                  <TableBody>
                    {product?.certificates.map((certificate, index) => (
                      <TableRow key={index}>
                        <TableCell width="30%">
                          <Box
                            display="flex"
                            alignItems="center"
                            flexDirection="row"
                            gap="8px"
                          >
                            <CheckCircleRounded color="success" />
                            <RowName>{certificate.name}</RowName>
                          </Box>
                        </TableCell>
                        <TableCell>{certificate.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </PanelBody>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
